/**
 * Overpanel view. This is a full-screen replacement of interactive modals, and
 * modals with inline flows. Subviews render inside the overpanel.
 */

define([
	'jquery',
	'underscore',
	'BaseView',
	'backbone'
], function ($, _, BaseView, Backbone) {

	'use strict';

	var Overpanel = BaseView.extend({

		el: '#overpanel',

		events: {
			'click .close': 'abortOverpanel',

			'focus': 'setTabables'
		},

		/**
		 * Initialize the overpanel
		 */
		initialize: function () {
			this.$body = $('body');
			this.isFlow = this.$body.data('isflow');

			this.canUseEsc = true;
			this.isOpen = false;
			this.isRenderEnabled = true;

			if (this.isFlow) {
				this.$body.removeClass('hasSpinner');
			}
			// Default this value to false. Override it on view initialization if the view should be cleared when closed.
			this.clearContentsOnClose = false;

			// set pusher that pushed the state
			this.listenTo(Backbone, 'pushedState', this.setPusher);
			// trigger toggleLoading event if in an overpanel instead of using BaseView's
			this.listenTo(Backbone, 'toggleLoading', this.toggleLoading);
			this.listenTo(Backbone, 'overpanel:hide', this.hideOverpanel);

			/**
			 * This is feature detection for animation to test whether or not the browser
			 * supports the listening to the animationend event
			 */
			this.hasAnimationEnd = document.body.style.animation === '';
		},

		/**
		 * Call the subview's beforeRender before the overpanel is rendered and shown
		 */
		beforeRender: function () {
			this.view.beforeRender();
		},

		/**
		 * Call the subview's afterRender after the overpanel is rendered and show the overpanel
		 */
		afterRender: function () {
			var $modal = this.$('.modal'),
				self = this;

			this.view.afterRender();

			if (this.isBlankView) {
				this.isBlankView = false;
			} else {
				this.showOverpanel();
			}

			// check if the overpanel contains a fullscreenTooltip modal
			if ($modal.length) {
				$modal.on('shown.bs.modal', function () {
					// we need to stop listening to ESC when the fullscreenTooltip modal is shown
					self.shouldListenToEsc(false);
				}).on('hidden.bs.modal', function () {
					// need to defer resuming listening to ESC when the fullscreenTooltip modal is hidden
					setTimeout(function () {
						self.shouldListenToEsc(true);
					}, 0);
				});
			}
		},

		/**
		 * Sets the pusher that changed the route
		 * @param $pusher the target that pushed the route
		 */
		setPusher: function ($pusher) {
			this.$pusher = $pusher;
		},

		/**
		 * Toggle the loading spinner
		 * Adds the hasSpinner class to the overpanel view's $el
		 * Overrides BaseView
		 * @param loading - boolean true or false
		 * @return this - the view
		 */
		toggleLoading: function (loading) {
			// if the overpanel is open and loading, don't listen to esc,
			// but turn on listening to esc after loading
			if (this.isOpen) {
				this.shouldListenToEsc(!loading);
			}

			this.$el.toggleClass('hasSpinner', loading);

			return this;
		},

		/**
		 * Return if an overpanel exists
		 * @returns {boolean} exists
		 */
		exists: function () {
			return this.$el.length ? true : false;
		},

		/**
		 * Set's the overpanel's content (template, model, collection) and render it
		 * @param view - the view to render inside the overpanel
		 * @param options - is an optional object it could have the property of
		 *              'classname' - the class we can pass to overpanel to add with body tag when the overpanel opens
		 *              'suppressRender' - flag allows to prevent automatic rendering, false by default
		 * @returns {Overpanel}
		 */
		setContent: function (view, options) {
			// exit early if rendering isn't currently enabled
			if (!this.isRenderEnabled) {
				return this;
			}

			// the current view in the overpanel
			this.view = view;
			options = options || {};

			// set the element of the view being rendered in the overpanel to #overpanel
			this.view.setElement(this.$el);

			// This class will be added to body tag so that overpanel user can override the css specific to their flow
			this.className = options && options.className || '';

			this.template = view.template;
			if (view.model) {
				this.model = view.model;
			} else {
				this.collection = view.collection;
			}

			this.serialize = $.proxy(view.serialize, view);

			if (!(options.suppressRender)) {
				this.render();
			}

			this.trackPageImpression(options.pageName, options.pageName2, options.fptiCustomTags);

			return this;
		},

		trackPageImpression: function (pageName, pageName2, fptiCustomTags) {
			var template = pageName || this.view.template,
				impressionType = pageName ? 'trackImpression' : 'trackOverpanelImpression';

			if (template) {
				// When overpanel page is loaded directly, Analytics is not available and causes error.
				// Ex: try this to replicate this issue: http://localhost.paypal.com:8000/myaccount/wallet/card/CARD-ID/edit
				setTimeout(function() {
					Backbone.trigger(impressionType, template, pageName2, fptiCustomTags);
				}, 0);
			}

		},

		/**
		 * Sets the current tabables of the open overpanel
		 */
		setTabables: function () {
			this.$tabables = this.$el.find('a, button, input, select, textarea, [tabindex=0]')
				.filter(':visible:not(:disabled)');

			this.$footerLinks = $('.footer a').filter(':visible');
		},

		/**
		 * Manages the focus inside of the open overpanel
		 * @param event
		 */
		manageFocus: function (event) {
			var $focusedElement = $(document.activeElement),
				onFirstPanel = $focusedElement.is(this.$tabables.eq(0)),
				onLastPanel = $focusedElement.is(this.$tabables.eq(this.$tabables.length - 1)),
				onFirstFooter = $focusedElement.is(this.$footerLinks.eq(0)),
				onLastFooter = $focusedElement.is(this.$footerLinks.eq(this.$footerLinks.length - 1));

			if (event.which === 9) { // tab key
				if (event.shiftKey) {
					if (onFirstPanel) { // + shift key
						event.preventDefault(); // each time when set focus manually, should prevent default focus by tap
						this.$footerLinks.eq(this.$footerLinks.length - 1).focus();
					}
					if (onFirstFooter) {
						event.preventDefault();
						this.$tabables.eq(this.$tabables.length - 1).focus();
					}
				} else {
					if (onLastFooter) {
						event.preventDefault();
						this.$el.focus();
					}
					if (onLastPanel) {
						event.preventDefault();
						this.$footerLinks.eq(0).focus();
					}
				}
			}
		},

		/**
		 * Show the overpanel and add keyup ESC listener to body
		 */
		showOverpanel: function () {

			var self = this,
				animationType = this.isFlow ? 'fadeIn' : 'fadeInUpBig';

			if (this.className) {
				this.$body.addClass(this.className);
			}

			this.$el.removeClass('fadeOutDownBig hide');

			/*
			 *	This is required as there is a CSS cross browser issue with Chrome & Safari, when sidepanel is on a overpanel
			 */
			if (!this.isOpen) {
				// overpanelOpen class disables scrolling on the body
				this.$body.addClass('overpanelOpening');
				this.$el.addClass(animationType);
			}

			this.$el.attr('aria-hidden', false);

			this.isOpen = true;

			// Wait for the animation to finish and then
			// removes the classes and style needed for the animation when done
			// Either do this with the animationend event or setTimeout
			// depending on the browser support
			if (this.hasAnimationEnd) {
				this.$el.one('animationend', self.animationEnd.bind(self));
			} else {
				setTimeout(self.animationEnd.bind(self), 1000); // The time to complete the animation
			}
			return this;
		},

		clearContents: function () {
			if (this.clearContentsOnClose) {
				if (!this.BlankView) {
					this.BlankView = BaseView.extend({
						template: 'widgets/overpanel'
					});
				}
				this.isBlankView = true;
				this.setContent(new this.BlankView());
			}
			return this;
		},

		/**
		 * After fade up in animation ends, focus on the open overpanel
		 * After fade out down animation ends, add the hide class on the overpanel
		 */
		animationEnd: function () {
			var self = this,
				$overpanel = this.$el;

			if (self.isOpen) {
				Backbone.trigger('shouldHideContents', true);

				// (accessibility) put focus on the overpanel so contents are in immediate tab order
				$overpanel.focus();
				self.shouldApplyA11Y(true);

				// set the $element to focus on after you close the overpanel
				self.$focusOnClose = self.$pusher;

				if (self.canUseEsc) {
					self.shouldListenToEsc(true);
				}
				self.$body.removeClass('overpanelOpening').scrollTop(0);
				setTimeout(function () {
					// scrollTop fixes an iOS bug with position: absolute on the overpanel.
					self.$body.addClass('overpanelOpen');
				}, 30);
			} else {
				self.hide();
			}
		},

		hide: function () {
			this.$el.addClass('hide').removeClass('fadeOutDownBig');
			this.$body.removeClass('overpanelClosing');

			// the element to focus on is undefined if deep linking to an overpanel
			this.$focusOnClose = this.$focusOnClose || this.$body;
			this.$focusOnClose.focus();

			if (this.clearContentsOnClose) {
				this.clearContents();
			}

			Backbone.trigger('trackImpression');
		},

		/**
		 * Assume that overpanel flow was aborted, since overpanel was manually closed.
		 * Trigger 'overpanel:abort' event and call hideOverpanel() and pass through arguments.
		 */
		abortOverpanel: function () {
			// convert arguments into actual array
			var args = [].slice.call(arguments),
				options = { closedManually: true };

			// fire abort event since we assume flow was aborted
			Backbone.trigger('overpanel:abort');

			// if the second argument is an object already...
			args[1] = typeof args[1] === 'object' ?
				// add options to existing object,
				_.assign({}, args[1], options) :
				// otherwise just assign options
				options;

			this.hideOverpanel.apply(this, args);
		},

		/**
		 * Fade out down the overpanel
		 * @param event - Optional
		 * @param config - If config = {stopAutoNav: true}, overpanel doesn't auto-navigate to href in overpanel close button. - Optional
		 */
		hideOverpanel: function (event, config) {

			var self = this,
				$closeBtn = self.$('.close'),
				referrer,
				navigateUrl,
				flowId;

			config = config || {};

			if (this.isFlow) {
				// if hide was triggered by an event, we prevent default, which ensures we navigate to our intended url
				if (event) {
					event.preventDefault();
				}

				// grab flow info from body
				referrer = $('body').data('referrer') || '/myaccount/money/';
				flowId = $('body').data('mnw-flow-id') || null;

				// If User came from flow, overwrite the referrer with the urls for closing out flow
				if (config.closedManually && flowId) {
					referrer = '/myaccount/wallet/flow-cancel/' + flowId;
				} else if (!config.closedManually && flowId) {
					referrer = '/myaccount/wallet/flow-return/' + flowId;
				}

				// What's displayed on the browser. Need to remove /myaccount from it.
				navigateUrl = referrer.replace(/\/myaccount/, '');
				Backbone.history.navigate(navigateUrl, true);

				// prevent backbone from triggering a second .hideOverpanel() call on navigation
				this.clearContents();

				window.location.href = referrer;
				return;
			}


			function prepForAmination() {
				self.$body.removeClass('overpanelOpen').addClass('overpanelPrepForClose');
			}
			function closeAnimation() {
				self.$body.removeClass('overpanelPrepForClose').addClass('overpanelClosing');

				if (self.className) {
					self.$body.removeClass(this.className);
				}
				self.$el.removeClass('fadeInUpBig').addClass('fadeOutDownBig');
				self.$el.attr('aria-hidden', true);

				self.canUseEsc = true;

				// Fallback for browsers that do not trigger animationEnd (IE9)
				setTimeout($.proxy(function () {
					this.hide();
				}, self), 500);
			}

			if (event) {
				event.preventDefault();
			}

			if (self.isOpen) {
				self.isOpen = false;

				prepForAmination();
				setTimeout(closeAnimation, 500);
			}

			self.shouldListenToEsc(false);
			self.shouldApplyA11Y(false);


			// navigate to the close button's url - '/myaccount'
			// the $('.close') button is required in the overpanel template
			// wrap in a condition because sometimes this function mistakenly gets called when no content was rendered in the overpanel
			// if {config.stopAutoNav: true}, don't run this. Helps as this can cause event-collision problems with routes being double-navigated to.
			if ($closeBtn.length !== 0 && !config.stopAutoNav) {
				Backbone.history.navigate($closeBtn.attr('href').slice(10), {trigger: true});
			}

			Backbone.trigger('shouldHideContents', false);
			Backbone.trigger('overpanel:close');

			// Wait for the animation to finish and then
			// removes the classes and style needed for the animation when done
			// Either do this with the animationend event or setTimeout
			// depending on the browser support
			if (this.hasAnimationEnd) {
				this.$el.one('animationend', self.animationEnd.bind(self));
			} else {
				setTimeout(self.animationEnd.bind(self), 1000); // The time to complete the animation
			}
		},

		/**
		 * Hide overpanel and remove keydown event from $body
		 */
		closeOverpanel: function (event) {
			// if ESC button pressed
			if (event.which === 27) {
				this.abortOverpanel(); // close it via abort (since we assume flow was aborted)

				// track closing the overpanel
				Backbone.trigger('trackLink', this.$('.close'));
			}
		},

		/**
		 * Listen for keydown and close overpanel if ESC was pressed
		 */
		shouldListenToEsc: function (listen) {
			if (listen) {
				this.$body.on('keydown', $.proxy(this.closeOverpanel, this));
			} else {
				this.$body.off('keydown', $.proxy(this.closeOverpanel, this));
			}
		},

		/**
		 * toggle method to manage accessibility for overpanel with footer
		 */
		shouldApplyA11Y: function (apply) {
			if (apply) {
				$('#overpanel, #footer').on('keydown', $.proxy(this.manageFocus, this));
			} else {
				$('#overpanel, #footer').off('keydown', $.proxy(this.manageFocus, this));
			}
		}
	});

	return new Overpanel();

});



//////////////////
// WEBPACK FOOTER
// ./widgets/theoverpanel.js
// module id = 22
// module chunks = 0